// My Trading Journey → Am I profitable? (/app/journey/profitable): real money only. The verdict
// with its formula, spent vs money in, where the money went, the money statement (months,
// quarters or years), every account, and the ledger every number comes from. Everything is a sum
// of moneyLedger's rows (journey.js), on the side the Journey's switch shows and in its period.
// Clicking a number opens the ledger filtered to the rows behind it.
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { CATEGORY_NAMES, accountHistory, evaluationRecord } from './business';
import { ExpenseDialog } from './BusinessPage';
import { AccountStory, PastAccountForm } from './JourneyAccount';
import { CumulativeChart, Donut, FIRM_COLORS, GeneralCosts, JourneyStart, MissingCosts, Tile, WhereMoneyWent } from './JourneyOverview';
import { useJourney } from './JourneyStory';
import { COLUMN_NAMES, END_CAUSE_NAMES, columnOf, journeyOverview, moneyLedger, moneyStatement, periodRange, personalMoney, scopeTotals } from './journey';
import { dayKey, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const whole = (n) => usd.format(Math.round(Math.abs(n))).replace(/\.00$/, '');
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${whole(n)}`;
const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const monthLong = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' });
const dayText = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
const inRange = (date, range) => (!range.from || date >= range.from) && (!range.to || date <= range.to);
const groupName = (key, unit) => (unit === 'month' ? monthLong(key) : unit === 'quarter' ? key.replace('-', ' ') : key);
const keyOf = (date, unit) => (unit === 'year' ? date.slice(0, 4)
  : unit === 'quarter' ? `${date.slice(0, 4)}-Q${Math.floor((Number(date.slice(5, 7)) - 1) / 3) + 1}` : date.slice(0, 7));

const UNITS = [['month', 'Months'], ['quarter', 'Quarters'], ['year', 'Years']];
const OUTCOME = { failed: 'Blown', blown: 'Blown', passed: 'Passed', active: 'Active', ended: 'Ended', restarted: 'Restarted' };
const KINDS = [['all', 'Everything'], ['expense', 'Costs'], ['payout', 'Payouts'], ['trading', 'Personal trading'], ['transfer', 'Deposits and withdrawals']];
const KIND_NAMES = { expense: 'Cost', payout: 'Payout', trading: 'Personal trading', deposit: 'Deposit', withdrawal: 'Withdrawal' };

function csvCell(v) {
  const s = String(v === undefined || v === null ? '' : v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// The headline: the result in the period, the sum that makes it, and the last 3 months.
function Verdict({ ledger, statement, scope, range, period, onPick, history, totals, onScope }) {
  const { t } = useI18n();
  const net = statement.totals.net;
  const shown = ledger.filter((r) => !r.transfer && inRange(r.date, range));
  const paid = shown.filter((r) => r.kind === 'payout');
  const traded = shown.filter((r) => r.kind === 'trading');
  const costs = shown.filter((r) => r.kind === 'expense');
  const sumOf = (list) => list.reduce((s, r) => s + r.amount, 0);
  const tradeCount = traded.reduce((s, r) => s + r.trades, 0);
  const side = [
    scope !== 'personal' && <Tile key="paid" label={t('Total payouts')} value={whole(sumOf(paid))} tone="paid" onClick={() => onPick({ col: 'payouts' })}
      note={t(paid.length === 1 ? '{n} payout' : '{n} payouts', { n: paid.length })} />,
    scope !== 'prop' && <Tile key="traded" label={t('Personal trading')} value={signed(sumOf(traded))} tone={sumOf(traded) >= 0 ? 'paid' : 'spent'} onClick={() => onPick({ col: 'trading' })}
      note={t(tradeCount === 1 ? '{n} trade' : '{n} trades', { n: tradeCount })} />,
    <Tile key="spent" label={t('Total spent')} value={whole(sumOf(costs))} tone={costs.length ? 'spent' : ''} onClick={() => onPick({ kind: 'expense' })}
      note={!costs.length ? t('nothing logged yet') : t(scope === 'prop' ? 'evaluations, resets and fees' : scope === 'personal' ? 'data, platforms, courses and other trading costs' : 'evaluations, resets, fees, courses and tools')} />,
  ].filter(Boolean);
  const last3 = periodRange('last3');
  const recent = ledger.filter((r) => !r.transfer && inRange(r.date, last3)).reduce((s, r) => s + r.amount, 0);
  const terms = statement.columns.map((c) => [c, statement.totals.cells[c] || 0]).filter(([, v]) => v);
  const moneyIn = terms.reduce((s, [, v]) => s + Math.max(0, v), 0);
  const moneyOut = terms.reduce((s, [, v]) => s + Math.max(0, -v), 0);
  const pct = moneyOut ? Math.min(100, (moneyIn / moneyOut) * 100) : null;
  const label = { prop: 'Prop firms · payouts − what you paid the firms', personal: 'Personal · trading profit − costs', all: 'Everything · money in − money out' }[scope];
  return (
    <section className="jo-card jo-hero jm-verdict">
      <div className="jm-verdict-main">
      <span className="jo-tile-label">{t(label)}{period !== 'all' ? ` · ${t(period === 'last3' ? 'Last 3 months' : period === 'last12' ? 'Last 12 months' : period)}` : ''}</span>
      <p className={`jo-net ${tone(net)}`}>{signed(net)}</p>
      <p className="jo-hero-sub">
        {!terms.length ? t('Log what you paid and your payouts, and this shows whether trading is paying for itself.')
          : net >= 0 ? t('Profitable in real money: more came in than went out.')
            : t('{amount} to go until your trading pays for itself.', { amount: whole(net) })}
      </p>
      {terms.length > 0 && (
        <p className="jm-formula">
          {terms.map(([c, v], i) => (
            <span key={c}>
              {i > 0 && <span className="jm-op">{v < 0 ? '−' : '+'}</span>}
              {i === 0 && v < 0 && <span className="jm-op">−</span>}
              <button type="button" className="link-button" onClick={() => onPick({ col: c })}>{t(COLUMN_NAMES[c])} {whole(v)}</button>
            </span>
          ))}
          <span className="jm-op">=</span><strong className={tone(net)}>{signed(net)}</strong>
        </p>
      )}
      <div className="jm-verdict-row">
        <span>{t('Last 3 months')}: <b className={tone(recent)}>{signed(recent)}</b></span>
        {pct !== null && <span>{t('{pct}% of what went out has come back', { pct: Math.round(pct) })}</span>}
      </div>
      {pct !== null && <div className="jo-progress-track"><span style={{ width: `${pct}%` }} /></div>}
      {scope !== 'personal' && <MissingCosts history={history} />}
      {scope !== 'all' && totals && <GeneralCosts amount={totals.general} onScope={onScope} />}
      </div>
      <div className="jo-hero-side">{side}</div>
    </section>
  );
}

// The money statement: one row per month, quarter or year, every column clickable.
function Statement({ ledger, scope, range, onPick }) {
  const { t } = useI18n();
  const [unit, setUnit] = useState('month');
  const st = useMemo(() => moneyStatement(ledger, scope, unit, range), [ledger, scope, unit, range]);
  const cols = st.columns.filter((c) => st.totals.cells[c] || st.rows.some((r) => r.cells[c]));
  const rows = [...st.rows].reverse();
  function download() {
    const head = [t('Period'), ...cols.map((c) => t(COLUMN_NAMES[c])), t('Net'), t('Running total')];
    const body = st.rows.map((r) => [groupName(r.key, unit), ...cols.map((c) => (r.cells[c] || 0).toFixed(2)), r.net.toFixed(2), r.running.toFixed(2)]);
    const total = [t('Total'), ...cols.map((c) => (st.totals.cells[c] || 0).toFixed(2)), st.totals.net.toFixed(2), ''];
    const url = URL.createObjectURL(new Blob([[head, ...body, total].map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `market-matrix-statement-${scope}-${unit}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <section className="jo-card jm-statement">
      <div className="jo-card-head">
        <div>
          <h2>{t('Money statement')}</h2>
          <p className="field-hint">{t('Real money only, newest first. Click any amount to see the entries behind it.')}</p>
        </div>
        <div className="jm-head-actions">
          <div className="insights-tabs" role="group" aria-label={t('Show by')}>
            {UNITS.map(([id, label]) => (
              <button key={id} type="button" className={`secondary small${unit === id ? ' active' : ''}`} aria-pressed={unit === id} onClick={() => setUnit(id)}>{t(label)}</button>
            ))}
          </div>
          {st.rows.length > 0 && <button type="button" className="secondary small" onClick={download}>{t('Export CSV')}</button>}
        </div>
      </div>
      {!st.rows.length ? <p className="empty">{t('Nothing in this period yet.')}</p> : (
        <div className="table-wrap">
          <table className="biz-table jm-table">
            <thead>
              <tr><th>{t(unit === 'month' ? 'Month' : unit === 'quarter' ? 'Quarter' : 'Year')}</th>{cols.map((c) => <th key={c} className="num">{t(COLUMN_NAMES[c])}</th>)}<th className="num">{t('Net')}</th><th className="num">{t('Running total')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <th scope="row">{groupName(r.key, unit)}</th>
                  {cols.map((c) => (
                    <td key={c} className={`num ${tone(r.cells[c] || 0)}`}>
                      {r.cells[c] ? <button type="button" className="jm-cell" onClick={() => onPick({ col: c, key: r.key, unit })}>{signed(r.cells[c])}</button> : <span className="jm-dash">—</span>}
                    </td>
                  ))}
                  <td className={`num jm-net ${tone(r.net)}`}>{r.net ? <button type="button" className="jm-cell" onClick={() => onPick({ key: r.key, unit })}>{signed(r.net)}</button> : <span className="jm-dash">—</span>}</td>
                  <td className={`num ${tone(r.running)}`}>{signed(r.running)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">{t('Total')}</th>
                {cols.map((c) => <td key={c} className={`num ${tone(st.totals.cells[c] || 0)}`}>{st.totals.cells[c] ? signed(st.totals.cells[c]) : '—'}</td>)}
                <td className={`num jm-net ${tone(st.totals.net)}`}>{signed(st.totals.net)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

// Every prop account with what it cost, what it paid and how it ended, sortable and filterable,
// or the same totals by firm.
function PropAccounts({ history, range, onOpen, onAddPast, outcome, setOutcome, boxRef }) {
  const { t } = useI18n();
  const [view, setView] = useState('accounts');
  const [firm, setFirm] = useState('all');
  const [sort, setSort] = useState({ key: 'start', dir: -1 });
  const shown = history.filter((h) => (!range.from || (h.end || dayKey(new Date())) >= range.from) && (!range.to || h.start <= range.to));
  const firms = [...new Set(shown.map((h) => h.firm || t('Other')))].sort();
  const blown = (h) => h.outcome === 'failed' || h.outcome === 'blown';
  const rows = shown
    .filter((h) => outcome === 'all' || (outcome === 'blown' ? blown(h) : outcome === 'active' ? !h.end : h.outcome === outcome))
    .filter((h) => firm === 'all' || (h.firm || t('Other')) === firm)
    .map((h) => ({ ...h, net: h.payouts - h.spent }))
    .sort((a, b) => {
      const va = sort.key === 'name' ? a.account.name : a[sort.key];
      const vb = sort.key === 'name' ? b.account.name : b[sort.key];
      return (typeof va === 'string' ? va.localeCompare(vb) : (va || 0) - (vb || 0)) * sort.dir;
    });
  const byFirm = Object.values(shown.reduce((out, h) => {
    const f = (out[h.firm || 'Other'] = out[h.firm || 'Other'] || { firm: h.firm || t('Other'), accounts: 0, spent: 0, payouts: 0, blown: 0, passed: 0 });
    f.accounts += 1;
    f.spent += h.spent;
    f.payouts += h.payouts;
    if (blown(h)) f.blown += 1;
    if (h.outcome === 'passed') f.passed += 1;
    return out;
  }, {})).map((f) => ({ ...f, net: f.payouts - f.spent })).sort((a, b) => b.spent - a.spent);
  const head = (key, label, num) => (
    <th className={num ? 'num' : ''} aria-sort={sort.key === key ? (sort.dir > 0 ? 'ascending' : 'descending') : undefined}>
      <button type="button" className="jm-sort" onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }))}>
        {t(label)}{sort.key === key ? (sort.dir > 0 ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );
  return (
    <section className="jo-card" ref={boxRef}>
      <div className="jo-card-head">
        <div>
          <h2>{t('Every prop account')}</h2>
          <p className="field-hint">{t('What each account cost, what it paid you, and how it ended. Click a column to sort.')}</p>
        </div>
        <div className="jm-head-actions">
          <div className="insights-tabs" role="group" aria-label={t('Show')}>
            <button type="button" className={`secondary small${view === 'accounts' ? ' active' : ''}`} aria-pressed={view === 'accounts'} onClick={() => setView('accounts')}>{t('Accounts')}</button>
            <button type="button" className={`secondary small${view === 'firms' ? ' active' : ''}`} aria-pressed={view === 'firms'} onClick={() => setView('firms')}>{t('By firm')}</button>
          </div>
          {onAddPast && <button type="button" className="secondary small" onClick={onAddPast}>+ {t('Add a past account')}</button>}
        </div>
      </div>
      {view === 'accounts' ? (
        <>
          <div className="jm-filters">
            <div className="insights-tabs" role="group" aria-label={t('Outcome')}>
              {[['all', 'All'], ['active', 'Active'], ['passed', 'Passed'], ['blown', 'Blown'], ['ended', 'Ended']].map(([id, label]) => (
                <button key={id} type="button" className={`secondary small${outcome === id ? ' active' : ''}`} aria-pressed={outcome === id} onClick={() => setOutcome(id)}>{t(label)}</button>
              ))}
            </div>
            {firms.length > 1 && (
              <select value={firm} onChange={(e) => setFirm(e.target.value)} aria-label={t('Firm')}>
                <option value="all">{t('Every firm')}</option>
                {firms.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            )}
          </div>
          <div className="table-wrap">
            <table className="biz-table">
              <thead>
                <tr>{head('name', 'Account')}{head('start', 'Started')}{head('days', 'Days', true)}{head('spent', 'Cost', true)}{head('payouts', 'Payouts', true)}{head('net', 'Net', true)}<th>{t('Outcome')}</th><th className="hide-sm">{t('How it ended')}</th></tr>
              </thead>
              <tbody>
                {rows.map((h) => (
                  <tr key={h.account._id}>
                    <th scope="row"><button type="button" className="link-button" onClick={() => onOpen(h.account._id)}>{h.account.name}</button></th>
                    <td>{dayText(h.start)}</td>
                    <td className="num">{h.days}</td>
                    <td className="num">{h.spent ? whole(h.spent) : h.payouts ? <span className="jo-missing">{t('Cost missing')}</span> : '—'}</td>
                    <td className={`num ${h.payouts ? 'gain' : ''}`}>{h.payouts ? whole(h.payouts) : '—'}</td>
                    <td className={`num ${tone(h.net)}`}>{h.net ? signed(h.net) : '—'}</td>
                    <td><span className={`jo-badge ${h.outcome}`}>{t(OUTCOME[h.outcome] || h.outcome)}</span></td>
                    <td className="hide-sm">{h.account.endCause ? t(END_CAUSE_NAMES[h.account.endCause]) : h.end ? '—' : t('Still going')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length && <p className="empty">{t('No accounts match.')}</p>}
        </>
      ) : (
        <div className="table-wrap">
          <table className="biz-table">
            <thead><tr><th>{t('Firm')}</th><th className="num">{t('Accounts')}</th><th className="num">{t('Cost')}</th><th className="num">{t('Payouts')}</th><th className="num">{t('Net')}</th><th className="num hide-sm">{t('Passed')}</th><th className="num hide-sm">{t('Blown')}</th><th className="num hide-sm">{t('Cost per account')}</th></tr></thead>
            <tbody>
              {byFirm.map((f) => (
                <tr key={f.firm}>
                  <th scope="row">{f.firm}</th>
                  <td className="num">{f.accounts}</td>
                  <td className="num">{whole(f.spent)}</td>
                  <td className={`num ${f.payouts ? 'gain' : ''}`}>{f.payouts ? whole(f.payouts) : '—'}</td>
                  <td className={`num ${tone(f.net)}`}>{signed(f.net)}</td>
                  <td className="num hide-sm">{f.passed}</td>
                  <td className="num hide-sm">{f.blown}</td>
                  <td className="num hide-sm">{f.accounts ? whole(f.spent / f.accounts) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Personal accounts: what went in, what came out, the trading result and what they hold now.
function PersonalAccounts({ accounts, trades, range }) {
  const { t } = useI18n();
  const money = personalMoney(accounts, trades, range);
  if (!money.rows.length) return null;
  return (
    <section className="jo-card">
      <h2>{t('Personal accounts')}</h2>
      <p className="field-hint">{t('Deposits and withdrawals are your own money moving, so they aren’t in your result. Only trading profit and costs are.')}</p>
      <div className="table-wrap">
        <table className="biz-table">
          <thead><tr><th>{t('Account')}</th><th className="num">{t('Put in')}</th><th className="num">{t('Taken out')}</th><th className="num">{t('Trading P&L')}</th><th className="num">{t('Balance now')}</th></tr></thead>
          <tbody>
            {money.rows.map((r) => (
              <tr key={r.account._id}>
                <th scope="row">{r.account.name}</th>
                <td className="num">{whole(r.deposits)}</td>
                <td className="num">{whole(r.withdrawals)}</td>
                <td className={`num ${tone(r.pnl)}`}>{signed(r.pnl)}</td>
                <td className="num">{r.balance !== null ? whole(r.balance) : <span className="jm-dash" title={t('Add a starting balance to the account to see this')}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// What the money bought and what came of it, in the period: accounts bought, blown, evaluations
// passed, payouts, resets, and what each funded account cost in firm fees. Each opens its rows.
function AccountCounts({ history, ledger, range, onAccounts, onPick }) {
  const { t } = useI18n();
  const started = history.filter((h) => inRange(h.start, range));
  const ended = history.filter((h) => h.end && inRange(h.end, range));
  const blown = ended.filter((h) => h.outcome === 'failed' || h.outcome === 'blown');
  const evals = evaluationRecord(ended);
  const shown = ledger.filter((r) => !r.transfer && inRange(r.date, range));
  const payouts = shown.filter((r) => r.kind === 'payout');
  const resets = shown.filter((r) => r.kind === 'expense' && r.category === 'reset');
  const fees = shown.filter((r) => r.kind === 'expense' && ['evaluation', 'reset', 'activation', 'subscription'].includes(r.category)).reduce((s, r) => s - r.amount, 0);
  const funded = started.filter((h) => h.type === 'Funded').length;
  const causes = {};
  for (const h of blown) if (h.account.endCause) causes[h.account.endCause] = (causes[h.account.endCause] || 0) + 1;
  const top = Object.entries(causes).sort((a, b) => b[1] - a[1])[0];
  const avgDays = blown.length ? Math.round(blown.reduce((s, h) => s + h.days, 0) / blown.length) : null;
  const active = history.filter((h) => !h.end).length;
  if (!history.length) return null;
  return (
    <div className="jo-tiles">
      <Tile label={t('Accounts bought')} value={started.length} onClick={() => onAccounts('all')}
        note={[t(started.filter((h) => h.type === 'Evaluation').length === 1 ? '{n} evaluation' : '{n} evaluations', { n: started.filter((h) => h.type === 'Evaluation').length }), active ? t('{n} active now', { n: active }) : null].filter(Boolean).join(' · ')} />
      <Tile label={t('Blown accounts')} value={blown.length} tone={blown.length ? 'spent' : ''} onClick={() => onAccounts('blown')}
        note={!blown.length ? t('None yet') : [top && t('Most often: {cause}', { cause: t(END_CAUSE_NAMES[top[0]]) }), avgDays !== null && t('lasted {n} days on average', { n: avgDays })].filter(Boolean).join(' · ')} />
      <Tile label={t('Evaluations passed')} tone={evals.passed ? 'paid' : ''} onClick={() => onAccounts('passed')}
        value={evals.passed + evals.failed ? <>{evals.passed} <small className="jo-tile-of">{t('of {n}', { n: evals.passed + evals.failed })}</small></> : '—'}
        note={evals.passRate !== null ? t('{pct}% of the {n} that finished', { pct: Math.round(evals.passRate), n: evals.passed + evals.failed }) : t('No evaluation finished yet')} />
      <Tile label={t('Payouts received')} value={payouts.length} tone={payouts.length ? 'paid' : ''} onClick={() => onPick({ col: 'payouts' })}
        note={payouts.length ? whole(payouts.reduce((s, r) => s + r.amount, 0)) : t('No payouts yet')} />
      <Tile label={t('Resets bought')} value={resets.length} onClick={() => onPick({ category: 'reset' })}
        note={resets.length ? t('{amount} in resets', { amount: whole(resets.reduce((s, r) => s + r.amount, 0)) }) : t('No resets yet')} />
      <Tile label={t('Cost per funded account')} value={funded ? whole(fees / funded) : '—'}
        note={funded ? t('{fees} in firm fees for {n} funded', { fees: whole(fees), n: funded }) : t('No funded account yet')} />
    </div>
  );
}

// Spending by firm, as a wheel; clicking a firm shows what was spent with it.
function FirmWheel({ ledger, range, onPick }) {
  const { t } = useI18n();
  const firms = {};
  for (const r of ledger) if (r.kind === 'expense' && inRange(r.date, range)) firms[r.firm || 'Other'] = (firms[r.firm || 'Other'] || 0) - r.amount;
  const rows = Object.entries(firms).map(([firm, spent]) => ({ firm, spent })).sort((a, b) => b.spent - a.spent);
  const total = rows.reduce((s, f) => s + f.spent, 0);
  if (!total) return null;
  return (
    <section className="jo-card">
      <h2>{t('Spending by firm')}</h2>
      <Donut rows={rows} total={total} />
      <ul className="jo-firms">
        {rows.map((f, i) => (
          <li key={f.firm}>
            <span><i style={{ background: FIRM_COLORS[i % FIRM_COLORS.length] }} /><button type="button" className="link-button" onClick={() => onPick({ firm: f.firm })}>{f.firm === 'Other' ? t('Other') : f.firm}</button></span>
            <strong>{whole(f.spent)}</strong>
          </li>
        ))}
      </ul>
    </section>
  );
}

// The ledger: every entry the page is built from, filtered by a click elsewhere or by hand.
function Ledger({ ledger, scope, range, filter, onFilter, onEdit, onDelete, ledgerRef }) {
  const { t } = useI18n();
  const [kind, setKind] = useState('all');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(50);
  const q = query.trim().toLowerCase();
  const rows = ledger.filter((r) => inRange(r.date, range))
    .filter((r) => kind === 'all' || (kind === 'transfer' ? r.transfer : r.kind === kind))
    .filter((r) => !filter || ((!filter.col || columnOf(r, scope) === filter.col) && (!filter.key || (!r.transfer && keyOf(r.date, filter.unit) === filter.key))
      && (!filter.category || r.category === filter.category) && (!filter.kind || r.kind === filter.kind) && (!filter.firm || (r.kind === 'expense' && (r.firm || 'Other') === filter.firm))))
    .filter((r) => !q || [r.account, r.firm, r.note, r.category && CATEGORY_NAMES[r.category], KIND_NAMES[r.kind]].some((v) => v && t(v).toLowerCase().includes(q)))
    .reverse();
  const total = rows.filter((r) => !r.transfer).reduce((s, r) => s + r.amount, 0);
  const what = (r) => (r.kind === 'expense' ? t(CATEGORY_NAMES[r.category] || r.category) : r.kind === 'trading' ? t(r.trades === 1 ? 'Personal trading · {n} trade' : 'Personal trading · {n} trades', { n: r.trades })
    : r.note === 'Starting balance' ? t('Starting balance') : t(KIND_NAMES[r.kind]));
  const filterName = filter && [filter.col && t(COLUMN_NAMES[filter.col]), filter.category && t(CATEGORY_NAMES[filter.category]), filter.kind && t(KIND_NAMES[filter.kind]),
    filter.firm && t('Spent with {firm}', { firm: filter.firm }), filter.key && groupName(filter.key, filter.unit)].filter(Boolean).join(' · ');
  function download() {
    const head = ['Date', 'Type', 'What', 'Firm', 'Account', 'Amount (USD)', 'Counted in result', 'Note'];
    const body = [...rows].reverse().map((r) => [r.date, KIND_NAMES[r.kind], r.kind === 'expense' ? CATEGORY_NAMES[r.category] || r.category : r.kind === 'trading' ? `${r.trades} trades` : '', r.firm, r.account, r.amount.toFixed(2), r.transfer ? 'No (your own money moving)' : 'Yes', r.note]);
    const url = URL.createObjectURL(new Blob([[head, ...body].map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `market-matrix-ledger-${scope}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <section className="jo-card jm-ledger" ref={ledgerRef}>
      <div className="jo-card-head">
        <div>
          <h2>{t('Ledger')}</h2>
          <p className="field-hint">{t('Every entry behind the numbers above. Deposits and withdrawals show here but aren’t counted.')}</p>
        </div>
        <button type="button" className="secondary small" onClick={download}>{t('Export CSV')}</button>
      </div>
      <div className="jm-filters">
        <div className="insights-tabs" role="group" aria-label={t('Show')}>
          {KINDS.filter(([id]) => scope !== 'prop' || (id !== 'trading' && id !== 'transfer')).map(([id, label]) => (
            <button key={id} type="button" className={`secondary small${kind === id ? ' active' : ''}`} aria-pressed={kind === id} onClick={() => setKind(id)}>{t(label)}</button>
          ))}
        </div>
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('Search account, firm or note')} aria-label={t('Search the ledger')} />
      </div>
      {filter && (
        <p className="jm-filter-chip">
          {t('Showing: {what}', { what: filterName })} · <b className={tone(total)}>{signed(total)}</b>
          <button type="button" className="link-button" onClick={() => onFilter(null)}>{t('Show everything')}</button>
        </p>
      )}
      <div className="table-wrap">
        <table className="biz-table">
          <thead><tr><th>{t('Date')}</th><th>{t('What')}</th><th className="hide-sm">{t('Firm or account')}</th><th className="num">{t('Amount')}</th><th className="hide-sm">{t('Note')}</th><th /></tr></thead>
          <tbody>
            {rows.slice(0, limit).map((r) => (
              <tr key={r.id} className={r.transfer ? 'jm-transfer' : ''}>
                <td>{dayText(r.date)}</td>
                <td>{what(r)}</td>
                <td className="hide-sm">{[r.firm, r.account].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ') || '—'}</td>
                <td className={`num ${r.transfer ? '' : tone(r.amount)}`}>{signed(r.amount)}{r.transfer && <small className="jm-not-counted"> {t('not counted')}</small>}</td>
                <td className="hide-sm">{r.note && r.note !== 'Starting balance' ? r.note : ''}</td>
                <td className="jm-row-actions">{r.kind === 'expense' && onEdit && (
                  <>
                    <button type="button" className="link-button" onClick={() => onEdit(r.expense)}>{t('Edit')}</button>
                    <button type="button" className="link-button biz-delete" onClick={() => onDelete(r.expense)}>{t('Delete')}</button>
                  </>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!rows.length && <p className="empty">{t('Nothing matches.')}</p>}
      {rows.length > limit && <button type="button" className="secondary small jm-more" onClick={() => setLimit((n) => n + 100)}>{t('Show more ({n} left)', { n: rows.length - limit })}</button>}
    </section>
  );
}

export default function JourneyMoney({ accounts, trades, onAccountAdded, onOpenTrades, onSessionExpired }) {
  const { t } = useI18n();
  const { scope, setScope, allAccounts, allTrades, scopeExpenses, period = 'all' } = useJourney();
  const [allExpenses, setAllExpenses] = useState(null);
  const [filter, setFilter] = useState(null);
  const [editing, setEditing] = useState(null);
  const [story, setStory] = useState(null);
  const [adding, setAdding] = useState(false);
  const ledgerRef = useRef(null);
  const accountsRef = useRef(null);
  const [outcome, setOutcome] = useState('all');
  const load = () => api('/expenses').then(setAllExpenses).catch((err) => (err.status === 401 && onSessionExpired ? onSessionExpired() : setAllExpenses((l) => l || [])));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const expenses = useMemo(() => (allExpenses ? scopeExpenses(allExpenses) : []), [allExpenses, scope, allAccounts]); // eslint-disable-line react-hooks/exhaustive-deps
  const range = useMemo(() => periodRange(period), [period]);
  const ledger = useMemo(() => moneyLedger(accounts, trades, expenses), [accounts, trades, expenses]);
  const statement = useMemo(() => moneyStatement(ledger, scope, 'month', range), [ledger, scope, range]);
  const history = useMemo(() => accountHistory(accounts, trades, expenses), [accounts, trades, expenses]);
  const totals = useMemo(() => (setScope && allExpenses ? scopeTotals(allAccounts, allTrades, allExpenses) : null), [setScope, allAccounts, allTrades, allExpenses]);
  // The running totals for the chart: money out against money in (payouts, plus personal trading
  // on All together and Personal), month by month through the period.
  const chart = useMemo(() => {
    let out = 0;
    let got = 0;
    const months = {};
    for (const r of ledger) {
      if (r.transfer) continue;
      const m = r.date.slice(0, 7);
      if (r.kind === 'expense') out -= r.amount;
      else got += r.amount;
      months[m] = { month: m, spentTotal: out, paidTotal: got };
    }
    const list = [];
    let last = { spentTotal: 0, paidTotal: 0 };
    for (const g of moneyStatement(ledger, scope, 'month', {}).rows) {
      last = months[g.key] || last;
      list.push({ month: g.key, spentTotal: last.spentTotal, paidTotal: last.paidTotal });
    }
    return list.filter((m) => inRange(`${m.month}-15`, { from: range.from && `${range.from.slice(0, 7)}-01`, to: range.to }));
  }, [ledger, scope, range]);
  const cats = useMemo(() => {
    const out = {};
    for (const r of ledger) if (r.kind === 'expense' && inRange(r.date, range)) out[r.category] = (out[r.category] || 0) - r.amount;
    return Object.entries(out).map(([category, amount]) => ({ category, amount })).sort((a, b) => b.amount - a.amount);
  }, [ledger, range]);
  const breakEven = chart.find((m) => m.paidTotal > 0 && m.paidTotal >= m.spentTotal);
  const overview = useMemo(() => journeyOverview(accounts, trades, expenses), [accounts, trades, expenses]);

  function pick(f) {
    setFilter(f);
    setTimeout(() => ledgerRef.current && ledgerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
  }
  async function remove(expense) {
    if (!window.confirm(t(expense.repeat === 'monthly' ? 'Delete this monthly cost? Every month of it goes.' : 'Delete this expense?'))) return;
    try {
      await api(`/expenses/${expense._id}`, { method: 'DELETE' });
      saved();
    } catch (err) {
      if (err.status === 401 && onSessionExpired) onSessionExpired();
      else window.alert(err.message);
    }
  }
  function showAccounts(o) {
    setOutcome(o);
    setTimeout(() => accountsRef.current && accountsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
  }
  function saved() {
    setEditing(null);
    load();
    window.dispatchEvent(new Event('mm-expenses-changed'));
  }

  if (!allExpenses) return <p className="empty">{t('Loading…')}</p>;
  const inLabel = scope === 'prop' ? 'Paid out' : scope === 'personal' ? 'Personal trading' : 'Payouts + personal trading';
  return (
    <div className="jo jm">
      <JourneyStart o={overview} accounts={accounts} trades={trades} expenses={expenses} onAddPast={() => setAdding(true)} />
      <div className="jo-top">
        <Verdict ledger={ledger} statement={statement} scope={scope} range={range} period={period} onPick={pick} history={history} totals={totals} onScope={setScope} />
        <WhereMoneyWent cats={cats} onPick={(category) => pick({ category })} />
      </div>
      {scope !== 'personal' && <AccountCounts history={history} ledger={ledger} range={range} onAccounts={showAccounts} onPick={pick} />}
      {chart.length > 0 && (
        <div className={scope !== 'personal' ? 'jo-mid' : ''}>
          <section className="jo-card">
            <h2>{t(scope === 'prop' ? 'Spent vs paid out' : 'Spent vs money in')}</h2>
            <p className="field-hint">{t('Running totals since your first entry. Where the lines cross, your trading has paid for itself.')}</p>
            <CumulativeChart months={chart} breakEvenMonth={breakEven ? breakEven.month : null} inLabel={inLabel} />
          </section>
          {scope !== 'personal' && <FirmWheel ledger={ledger} range={range} onPick={pick} />}
        </div>
      )}
      <Statement ledger={ledger} scope={scope} range={range} onPick={pick} />
      {scope !== 'personal' && history.length > 0 && <PropAccounts history={history} range={range} onOpen={setStory} onAddPast={() => setAdding(true)} outcome={outcome} setOutcome={setOutcome} boxRef={accountsRef} />}
      {scope !== 'prop' && <PersonalAccounts accounts={accounts} trades={trades} range={range} />}
      <Ledger ledger={ledger} scope={scope} range={range} filter={filter} onFilter={setFilter} onEdit={setEditing} onDelete={remove} ledgerRef={ledgerRef} />
      {editing && <ExpenseDialog expense={editing} accounts={accounts} onClose={() => setEditing(null)} onSaved={saved} onSessionExpired={onSessionExpired} />}
      {story && <AccountStory accountId={story} accounts={accounts} trades={trades} expenses={expenses} onClose={() => setStory(null)} onOpenTrades={onOpenTrades} />}
      {adding && (
        <PastAccountForm onClose={() => setAdding(false)} onSessionExpired={onSessionExpired}
          onSaved={(account) => { setAdding(false); load(); if (onAccountAdded) onAccountAdded(account); setStory(account._id); }} />
      )}
    </div>
  );
}

